import React, { useEffect, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import { BusinessUserProfileForm } from "@tootsweet/model/entities/BusinessUserProfileForm";
import { SECTOR_NAME } from "@tootsweet/model/enums/SectorName";
import KC_UIText from "../../../designsystem/typography/KC_UIText/KC_UIText";
import KC_Input from "../../../designsystem/KC_FormInput/KC_Input";
import KC_Button, {
  KC_ButtonSize,
  KC_ButtonType
} from "../../../designsystem/KC_Button/KC_Button";
import KC_Separator from "../../../designsystem/KC_Separator/KC_Separator";
import { KC_Sizes, KC_Weights } from "../../../designsystem/ds_commons";
import { tt } from "../../../utils/i18n/i18n";
import { CLIENTS_PER_DAY_ANSWER, SECTOR_LIST } from "../../../utils/constants";
import KC_PhoneInput from "../../../designsystem/KC_PhoneInput/KC_PhoneInput";
import {
  DEFAULT_CURRENCY,
  getCurrencyFromCountryCode
} from "@tootsweet/model/enums/Currencies";
import { infoOnboardingSchema } from "../model/validationSchema/infoOnboardingValidationSchema";
import { getCountriesByLanguage } from "../../../utils/i18n/getCountriesByLang";
import {
  useCountryCodeSelector,
  useCurrencySelector
} from "../../../redux/actions/app";

interface OnboardingFormProps {
  initialValues: BusinessUserProfileForm;
  onSubmit: (values: BusinessUserProfileForm) => void;
  submitButtonText?: string;
}

export default function OnboardingForm(props: OnboardingFormProps) {
  const currency = useCurrencySelector();
  const countryCode = useCountryCodeSelector();

  const [dCurrency, setDCurrency] = useState("");

  const { ALL_COUNTRIES_NAME, ALL_COUNTRIES_CODE } = useMemo(() => {
    return getCountriesByLanguage();
  }, []);

  useEffect(() => {
    const finalCountryCode =
      props.initialValues?.shippingAddress?.countryCode ?? countryCode;
    if (finalCountryCode) {
      setDCurrency(getCurrencyFromCountryCode(finalCountryCode));
    }
  }, [countryCode, props.initialValues]);

  return (
    <Formik<BusinessUserProfileForm>
      initialValues={props.initialValues}
      onSubmit={props.onSubmit}
      validationSchema={infoOnboardingSchema}
    >
      {({ values, setFieldValue, errors, touched, setFieldTouched }) => {
        const displayAvgBasket =
          values.customerType === SECTOR_NAME.BAR ||
          values.customerType === SECTOR_NAME.CAFE ||
          values.customerType === SECTOR_NAME.RESTAURANT;
        return (
          <Form className="d-flex flex-column w-100 position-relative">
            <div>
              <label htmlFor="placeName">
                <KC_UIText
                  weight={KC_Weights.md}
                  text={tt("jp_onboarding_form_label_place_name")}
                />
              </label>
              <KC_Input
                id={"placeName"}
                htmlFor={"placeName"}
                placeholder={tt("jp_onboarding_form_label_place_name")}
                setFieldValue={setFieldValue}
                value={values && values.placeName ? values.placeName : ""}
                required={true}
              />
            </div>
            <KC_Separator size={KC_Sizes.sm} />
            <div className="double-inputs-container">
              <div className="w-100">
                <label htmlFor="contactFirstName">
                  <KC_UIText
                    weight={KC_Weights.md}
                    text={tt("jp_onboarding_form_label_first_name")}
                  />
                </label>
                <KC_Input
                  id="contactFirstName"
                  htmlFor="contactFirstName"
                  placeholder={tt("jp_onboarding_form_label_first_name")}
                  type="text"
                  setFieldValue={(_, value) => {
                    void setFieldValue("contactFirstName", value);
                    void setFieldValue(
                      "contactName",
                      `${value} ${values.contactLastName || ""}`.trim()
                    );
                  }}
                  required={true}
                  value={values.contactFirstName || ""}
                />
              </div>
              <div className="w-100">
                <label htmlFor="contactLastName">
                  <KC_UIText
                    weight={KC_Weights.md}
                    text={tt("jp_onboarding_form_label_last_name")}
                  />
                </label>
                <KC_Input
                  id="contactLastName"
                  htmlFor="contactLastName"
                  placeholder={tt("jp_onboarding_form_label_last_name")}
                  type="text"
                  setFieldValue={(_, value) => {
                    void setFieldValue("contactLastName", value);
                    void setFieldValue(
                      "contactName",
                      `${values.contactFirstName || ""} ${value}`.trim()
                    );
                  }}
                  required={true}
                  value={values.contactLastName || ""}
                />
              </div>
            </div>
            <KC_Separator size={KC_Sizes.sm} />
            <div style={{ width: "100%" }}>
              <label htmlFor="contactPhoneNumber">
                <KC_UIText
                  weight={KC_Weights.md}
                  text={tt(
                    "jp_onboarding_form_label_operational_contact_phone"
                  )}
                />
              </label>
              <KC_PhoneInput
                value={
                  values && values.contactPhoneNumber
                    ? values.contactPhoneNumber
                    : ""
                }
                id={"contactPhoneNumber"}
                htmlFor="contactPhoneNumber"
                placeholder={""}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                touched={touched}
                errors={errors}
                required={true}
                defaultCountry={
                  (values.shippingAddress?.countryCode?.toLowerCase() as any) ||
                  "fr"
                }
                onCountryChange={(countryIso2) => {
                  const countryCode = countryIso2.toUpperCase();
                  void setFieldValue(
                    "shippingAddress.countryCode",
                    countryCode
                  );
                  void setFieldValue(
                    "dCurrency",
                    getCurrencyFromCountryCode(countryCode)
                  );
                }}
              />
            </div>
            <KC_Separator size={KC_Sizes.sm} />
            <div className="w-100">
              <div className="flex-grow-1">
                <label>
                  <KC_UIText weight={KC_Weights.md} text={tt("game_country")} />
                </label>
                <div className="kc-input-wrapper">
                  <select
                    className={"kc_input pl-3"}
                    onChange={(e) => {
                      void setFieldValue(
                        "shippingAddress.countryCode",
                        e.target.value
                      );
                      const curr = getCurrencyFromCountryCode(e.target.value);
                      setDCurrency(curr);
                      void setFieldValue("dCurrency", curr);
                    }}
                    id={"shippingAddressCountryCode"}
                    value={values.shippingAddress?.countryCode?.toUpperCase()}
                    required={true}
                  >
                    <React.Fragment>
                      <option value="">{tt("country_placeholder")}</option>
                      {ALL_COUNTRIES_NAME?.map((name, idx) => {
                        const val = ALL_COUNTRIES_CODE[idx];
                        return (
                          <option key={`country-${idx}-${val}`} value={val}>
                            {name}
                          </option>
                        );
                      })}
                    </React.Fragment>
                  </select>
                </div>
              </div>
            </div>
            <KC_Separator size={KC_Sizes.sm} />
            <div className="w-100">
              <div className="w-sm-100">
                <label htmlFor={"sector"}>
                  <KC_UIText
                    weight={KC_Weights.md}
                    text={tt("jp_onboarding_form_label_sector")}
                  />
                </label>
                <div className="kc-input-wrapper">
                  <select
                    id="onboarding-sector"
                    onChange={(e) => {
                      setFieldValue("customerType", e.target.value);
                    }}
                    value={values.customerType}
                    className="kc_input px-2"
                    required={true}
                  >
                    <option value=""></option>
                    {SECTOR_LIST.map((sector, idx) => {
                      return (
                        <option key={sector.value + idx} value={sector.value}>
                          {sector.name}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>
            {displayAvgBasket && (
              <>
                <KC_Separator size={KC_Sizes.sm} />
                <div className="w-100">
                  <div className="w-sm-100">
                    <label htmlFor={"avgBasket"}>
                      <KC_UIText
                        weight={KC_Weights.md}
                        text={tt("jp_onboarding_form_label_avg_basket")}
                      />
                    </label>

                    <KC_Input
                      id="avgBasket"
                      htmlFor="avgBasket"
                      placeholder={tt("jp_onboarding_form_label_avg_basket")}
                      type="number"
                      setFieldValue={setFieldValue}
                      required={true}
                      value={values.avgBasket}
                      appendElementText={
                        values.dCurrency ||
                        dCurrency ||
                        currency ||
                        DEFAULT_CURRENCY
                      }
                    />
                  </div>
                </div>
              </>
            )}
            <KC_Separator size={KC_Sizes.sm} />
            <div className="w-100">
              <div className="w-sm-100">
                <label htmlFor={"clientsPerDay"}>
                  <KC_UIText
                    weight={KC_Weights.md}
                    text={tt("jp_onboarding_form_label_clients_per_day")}
                  />
                </label>
                <div className="kc-input-wrapper">
                  <select
                    id="clientsPerDay"
                    onChange={(e) => {
                      setFieldValue("clientsPerDay", e.target.value);
                    }}
                    value={values.clientsPerDay}
                    className="kc_input px-2"
                    required
                  >
                    <option value=""></option>
                    {[
                      ...CLIENTS_PER_DAY_ANSWER,
                      {
                        name: "not_relevant",
                        displayName: tt("clients_per_day_not_relevant")
                      }
                    ].map((answer, idx) => {
                      return (
                        <option key={answer.name + idx} value={answer.name}>
                          {answer.displayName}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>
            <KC_Separator size={KC_Sizes.sm} />
            <KC_Button
              id="submit-btn"
              className="submit-btn mb-5 d-none d-sm-flex"
              text={props.submitButtonText || tt("create_my_free_game")}
              type={KC_ButtonType.Submit}
              size={KC_ButtonSize.Giant}
            />
            <div className="d-block d-sm-none position-sticky bg-white p-4 mobile-sticky-submit">
              <KC_Button
                id="submit-btn"
                className="submit-btn d-flex"
                text={props.submitButtonText || tt("create_my_free_game")}
                type={KC_ButtonType.Submit}
                size={KC_ButtonSize.Giant}
              />
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}
